import React, { useState, useEffect } from 'react';
import QuestionComponentFactory from './QuestionComponentFactory';
import QuestionCard from './question/QuestionCard';
import { useDispatch, useSelector } from 'react-redux';
import { getAllQuestions, saveAnswer, getAllAnswers, deleteAnswer } from '../actions/questionActions';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ProposalStatus} from '../enums/proposalStatus';
import { updateProposal, getAllProposals } from '../actions/proposalActions';

const Questionnaire = () => {
    const { proposalId } = useParams();
    const location = useLocation();
    const canEdit = location.state?.canEdit ?? false;
    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.user);
    const { questions, answers, loading, error } = useSelector(state => state.question);
    const { proposals } = useSelector(state => state.proposal);
    const [currentStep, setCurrentStep] = useState(0);
    const navigate = useNavigate();
    const [archivoErrors, setArchivoErrors] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            const stageId = 'F8DCD0C7-E379-4EE9-B5C5-280C1604B604';
            dispatch(getAllQuestions(userInfo, stageId));
            if(proposalId!='0000'){
                dispatch(getAllAnswers(userInfo, proposalId));
            }
        };

        fetchData();
    }, [dispatch, userInfo, proposalId]);

    const handleSubmit = async (e) => {
        e.preventDefault();        
        if (!canEdit) return;

        saveCurrentAnswer();
        const proposalData = {
            Id: proposalId,            
            estado: {
                Id: ProposalStatus.Pendiente.id
            },
            solicitud:{
                Id: ""
            }
        };

        dispatch(updateProposal(proposalData, userInfo, navigate));
    };

    const progress = (currentStep / (questions.length - 1)) * 100; // Calculate progress
    const saveCurrentAnswer = async () => {
        const currentSepQuestion = questions[currentStep];
        let newErrors = {};
        if (currentSepQuestion && currentSepQuestion.preguntas) {
            currentSepQuestion.preguntas.forEach(pregunta => {
                if (pregunta.tipo === 'Archivo' && pregunta.es_requerido) {
                    const answer = answers[pregunta.Id];
                    const archivoPrevio = answer && answer.answer === "archivo";
                    const archivoNuevo = answer && answer.answer && typeof answer.answer !== "string";
                    if (!archivoPrevio && !archivoNuevo) {
                        newErrors[pregunta.Id] = true;
                    }
                }
            });
        }
        setArchivoErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            // Hay errores, no avanzar
            return;
        }
        if(canEdit){
            const savePromises = [];
            
            currentSepQuestion.preguntas.forEach(mainQuestion => {
                const mainAnswer = answers[mainQuestion.Id];
                if (mainAnswer !== undefined) {
                    if (mainQuestion.idTipo.toLowerCase() === '226f383e-d1ae-40b2-964f-eb41585d1cff') {
                        mainAnswer.multipleAnswers?.forEach(( a ) => {
                            savePromises.push(save(mainQuestion, a));
                        })
                    }else{
                        savePromises.push(save(mainQuestion, mainAnswer));
                    }
                }
                mainQuestion.subpreguntas.forEach(subQuestion => {
                    const subAnswer = answers[subQuestion.Id];
                    if (subAnswer !== undefined) {
                        if (subAnswer.multipleAnswers && Array.isArray(subAnswer.multipleAnswers)) {
                            subAnswer.multipleAnswers.forEach(multipleAnswer => {
                                savePromises.push(save(subQuestion, multipleAnswer));
                            });
                        } else {
                            savePromises.push(save(subQuestion, subAnswer));
                        }
                    }
                });
            });
            
            await Promise.all(savePromises);
            
            if(proposalId !== '0000') {
                dispatch(getAllAnswers(userInfo, proposalId));
            }
        }
        setCurrentStep(prev => prev + 1);
    };
    
    const save = (question, currentAnswer) => {
        if (!canEdit) return Promise.resolve();
        
        // Si tiene multipleAnswers (caso destinatarios)
        if (currentAnswer && currentAnswer.multipleAnswers && Array.isArray(currentAnswer.multipleAnswers)) {
            const promises = currentAnswer.multipleAnswers.map(answ => {
                const answerData = {
                    propuesta: { Id: proposalId },
                    preguntaAsociada: { Id: question.Id },
                    respuesta: answ.answer,
                    observacion: answ.observacion,
                    esDicatamen: false
                };
                if (answ.answerId !== undefined) {
                    answerData.Id = answ.answerId;
                }
                return new Promise((resolve) => {
                    dispatch(saveAnswer(answerData, userInfo, null));
                    resolve();
                });
            });
            return Promise.all(promises);
        } else {
            // Lógica para respuestas simples o para micro/articulación
            let file = null;
            let rta = currentAnswer.answer || currentAnswer;
            if (question.tipo === "Archivo") {
                // Si la respuesta previa es "archivo", NO guardar nada
                if (currentAnswer && currentAnswer.answer === "archivo") {
                    return Promise.resolve();
                }
                // Si hay respuesta previa con answerId, usalo para el update
                const prevAnswer = answers[question.Id];
                if (!currentAnswer.answerId && prevAnswer && prevAnswer.answer === "archivo" && prevAnswer.answerId) {
                    currentAnswer.answerId = prevAnswer.answerId;
                }
                file = currentAnswer.answer;
                rta = "archivo";
            }
            
            const answerData = {
                propuesta: { Id: proposalId },
                preguntaAsociada: { Id: question.Id },
                opcionElegida: {},
                respuesta: rta,
                observacion: currentAnswer.observacion,
                tieneArchivo: question.tipo === "Archivo",
                rutaArchivo: question.tipo === "Archivo" ? currentAnswer.filePath : "",
                nombreArchivo: question.tipo === "Archivo" ? currentAnswer.fileName : "",
                esDicatamen: false
            };
            if (currentAnswer.answerId !== undefined) {
                answerData.Id = currentAnswer.answerId;
            }
            if(proposalId !== '0000') {
                return new Promise((resolve) => {
                    dispatch(saveAnswer(answerData, userInfo, file));
                    resolve();
                });
            }
            return Promise.resolve();
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {questions.length > 0 && (
                <div className='col-12 container-question questionnaire' >
                    <div className='row contFather' >
                        <div className='col-lg-8 col-md-12 col-sm-12 container-question-factory contSon'>
                            <div className='row'>
                                <div className='col-lg-8 col-md-12 col-sm-12'>
                                    {/* Progress bar */}
                                    <span className='paging'>Pagina {currentStep + 1} de {questions.length}</span>
                                    <div className="progress">
                                        <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{ width: `${progress}%` }}
                                            aria-valuenow={progress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        ></div>
                                    </div>
                                </div>
                                <div className='col-lg-4 col-md-12 col-sm-12 btn-questionnaire'>
                                    {currentStep > 0 && <button type="button" className='btn-question' onClick={() => setCurrentStep(prev => prev - 1)}>Anterior</button>}
                                    {currentStep < questions.length - 1 && <button type="button" className='btn-question' onClick={() => saveCurrentAnswer()}>Siguiente</button>}
                                    {currentStep === questions.length - 1 && canEdit && <button type="submit" className='btn-question-fin' onClick={(e) => handleSubmit(e)}>Finalizar</button>}
                                </div>
                            </div>
                            <div className='row mt-4'>
                                {<QuestionComponentFactory question={questions[currentStep]} currentStep={currentStep + 1} proposalId={proposalId} archivoErrors={archivoErrors} />}
                            </div>
                        </div>
                        <div className='col-lg-4 col-md-12 col-sm-12 center-container contSon'>
                            <QuestionCard questions={questions} currentStep={currentStep} />
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
};

export default Questionnaire;
